import { useQuery } from "@apollo/client";
import { loader } from "graphql.macro";
import { Checkbox, FormControlLabel, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";
import type { OhaioAgentChoice } from "../../lib/ohaio";
import { voiceTranslationEnabledForOrganization } from "../../lib/candyvoice";

const GET_ORGANIZATION_AGENTS_QUERY = loader(
  "../../schema/query/GetOrganizationAgents.graphql"
);

interface Data {
  organization: {
    id: string;
    options?: Record<string, any> | null;
    voiceTranslation: boolean;
    agents: OhaioAgentChoice[];
  };
}

interface Props {
  organizationId: string | null;
  // the agents chosen for the meeting, undefined for the default agents of
  // the organization
  value: string[] | undefined;
  onChange: (agentIds: string[]) => void;
}

// Choose the ohaio agents of a meeting, in the options of the meeting forms.
// Nothing is shown when the organization has no agent, or uses the CandyVoice
// voice translation instead of the ohaio livespeaker.
export const AgentsOptions: React.FC<Props> = ({
  organizationId,
  value,
  onChange,
}) => {
  const { data } = useQuery<Data>(GET_ORGANIZATION_AGENTS_QUERY, {
    variables: { id: organizationId },
    skip: !organizationId,
  });
  const agents = data?.organization.agents ?? [];
  if (agents.length === 0) return null;
  if (voiceTranslationEnabledForOrganization(data?.organization)) return null;

  const selected =
    value ?? agents.filter((agent) => agent.default).map((agent) => agent.id);

  return (
    <>
      <Typography sx={{ color: "text.secondary", pt: 1 }}>
        <FormattedMessage
          id="agents.context"
          defaultMessage="Business context"
        />
      </Typography>
      {agents.map((agent) => (
        <FormControlLabel
          key={agent.id}
          control={
            <Checkbox
              checked={selected.includes(agent.id)}
              onChange={(event) =>
                onChange(
                  // keep the order of the agents of the organization
                  agents
                    .map((a) => a.id)
                    .filter((id) =>
                      id === agent.id
                        ? event.target.checked
                        : selected.includes(id)
                    )
                )
              }
            />
          }
          label={agent.name}
        />
      ))}
    </>
  );
};
